import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Typography, Button, Container, CircularProgress } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import BottomNav from './components/navigation/BottomNav';
import MobileHeader from './components/navigation/MobileHeader';
import ProtectedRoute from './components/auth/ProtectedRoute';
// Eager load: Home (landing page)
import Home from './pages/Home';
// Lazy load: other pages
const CareersPage = lazy(() => import('./pages/CareersPage'));
const AssessmentFlow = lazy(() => import('./pages/AssessmentFlow'));
const ResultsPage = lazy(() => import('./pages/ResultsPage'));
const SharedResultsPage = lazy(() => import('./pages/SharedResultsPage'));
const TestResults = lazy(() => import('./pages/TestResults'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AthleteLoginPage = lazy(() => import('./pages/auth/AthleteLoginPage'));
const AthleteRegisterPage = lazy(() => import('./pages/auth/AthleteRegisterPage'));
const CompanyLoginPage = lazy(() => import('./pages/auth/CompanyLoginPage'));
const CompanyRegisterPage = lazy(() => import('./pages/auth/CompanyRegisterPage'));
const VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ProfileCreatePage = lazy(() => import('./pages/profile/ProfileCreatePage'));
const ProfileViewPage = lazy(() => import('./pages/profile/ProfileViewPage'));
const ProfileSettingsPage = lazy(() => import('./pages/profile/ProfileSettingsPage'));
const AthleteDashboardPage = lazy(() => import('./pages/athlete/AthleteDashboardPage'));
const ScoutInboxPage = lazy(() => import('./pages/athlete/ScoutInboxPage'));
const ScoutThreadPage = lazy(() => import('./pages/athlete/ScoutThreadPage'));
const CompanyDashboardPage = lazy(() => import('./pages/company/CompanyDashboardPage'));
const ProfileSearchPage = lazy(() => import('./pages/company/ProfileSearchPage'));
const ProfileDetailPage = lazy(() => import('./pages/company/ProfileDetailPage'));
const ScoutSentPage = lazy(() => import('./pages/company/ScoutSentPage'));

const theme = createTheme({
  palette: {
    primary: {
      main: '#0066cc', // 厚労省サイトに近い青色
    },
    secondary: {
      main: '#ff9800',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: [
      '"Hiragino Kaku Gothic ProN"',
      '"Hiragino Sans"',
      'Meiryo',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Arial',
      'sans-serif',
    ].join(','),
    fontSize: 15,
    h1: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h2: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.6,
    },
    body1: {
      fontSize: '0.95rem',
      lineHeight: 1.8,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.7,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 4,
          fontWeight: 600,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
});

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // プロフィール作成中はステップ間で上に戻さない
    if (pathname === '/profile/create') return;
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  return (
    <ErrorBoundary>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <BrowserRouter>
            <ScrollToTop />
            <MobileHeader />
            <Box sx={{ flex: 1, pb: { xs: '56px', md: 0 } }}>
              <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>}>
              <Routes>
                {/* 既存ルート */}
                <Route path="/" element={<Home />} />
                <Route path="/careers" element={<CareersPage />} />
                <Route path="/assessment" element={<AssessmentFlow />} />
                <Route path="/results" element={<ResultsPage />} />
                <Route path="/results/shared/:id" element={<SharedResultsPage />} />
                {process.env.NODE_ENV === 'development' && (
                  <Route path="/test-results" element={<TestResults />} />
                )}
                <Route path="/admin" element={<AdminPage />} />

                {/* 認証ルート */}
                <Route path="/auth/athlete-login" element={<AthleteLoginPage />} />
                <Route path="/auth/athlete-register" element={<AthleteRegisterPage />} />
                <Route path="/auth/company-login" element={<CompanyLoginPage />} />
                <Route path="/auth/company-register" element={<CompanyRegisterPage />} />
                <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
                <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />

                {/* プロフィール作成（アスリートのみ） */}
                <Route path="/profile/create" element={
                  <ProtectedRoute requiredType="athlete"><ProfileCreatePage /></ProtectedRoute>
                } />
                <Route path="/profile/view" element={
                  <ProtectedRoute requiredType="athlete"><ProfileViewPage /></ProtectedRoute>
                } />
                <Route path="/profile/settings" element={
                  <ProtectedRoute requiredType="athlete"><ProfileSettingsPage /></ProtectedRoute>
                } />

                {/* アスリートダッシュボード */}
                <Route path="/athlete/dashboard" element={
                  <ProtectedRoute requiredType="athlete"><AthleteDashboardPage /></ProtectedRoute>
                } />
                <Route path="/athlete/scouts" element={
                  <ProtectedRoute requiredType="athlete"><ScoutInboxPage /></ProtectedRoute>
                } />
                <Route path="/athlete/scouts/:threadId" element={
                  <ProtectedRoute requiredType="athlete"><ScoutThreadPage /></ProtectedRoute>
                } />

                {/* 企業ダッシュボード */}
                <Route path="/company/dashboard" element={
                  <ProtectedRoute requiredType="company"><CompanyDashboardPage /></ProtectedRoute>
                } />
                <Route path="/company/search" element={
                  <ProtectedRoute requiredType="company"><ProfileSearchPage /></ProtectedRoute>
                } />
                <Route path="/company/profile/:uuid" element={
                  <ProtectedRoute requiredType="company"><ProfileDetailPage /></ProtectedRoute>
                } />
                <Route path="/company/scouts" element={
                  <ProtectedRoute requiredType="company"><ScoutSentPage /></ProtectedRoute>
                } />

                {/* 404 */}
                <Route path="*" element={
                  <Container maxWidth="sm" sx={{ textAlign: 'center', py: 10 }}>
                    <Typography variant="h3" sx={{ mb: 1, fontWeight: 700, color: '#0066cc' }}>404</Typography>
                    <Typography variant="h6" sx={{ mb: 1 }}>ページが見つかりませんでした</Typography>
                    <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
                      お探しのページは存在しないか、移動した可能性があります。
                    </Typography>
                    <Button variant="contained" href="/">ホームに戻る</Button>
                  </Container>
                } />
              </Routes>
              </Suspense>
            </Box>
            <Box
              component="footer"
              sx={{
                py: 2,
                textAlign: 'center',
                bgcolor: '#333',
                color: '#fff',
              }}
            >
              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                &copy; Career Crea Co., Ltd. All Rights Reserved
              </Typography>
            </Box>
            <BottomNav />
          </BrowserRouter>
        </Box>
      </AuthProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
